import React, { useState } from 'react';
import clsx from 'clsx';
import { caretRight } from '../../../../../../assets/hostedassets';
import { isEmpty } from '../../../../../../utils/utils';
import { AuditedRowSpec } from '../../auditedStatementLayout';
import AuditedStatementTable from '../AuditedStatementTable/AuditedStatementTable';
import styles from './AuditedStatementSection.module.scss';

interface AuditedStatementSectionProps {
    title: string;
    layout: AuditedRowSpec[];
    tableData: { headers?: { name: string; key: string }[]; rows?: any[] };
    loading: boolean;
    /** Display unit for all figures ('Lakh' | 'Crore'), forwarded to the table for rescaling. */
    unit: string;
    defaultExpanded?: boolean;
}

/**
 * Collapsible section for ONE audited-financials statement (Balance Sheet / P&L / Cash Flow),
 * built to the RECUR-27010 redesign mock. Renders the NEW presentational `AuditedStatementTable`
 * — no icons, no per-row chevrons, no drill-down/RawTable/Edit-Classification (the mock has no
 * clickable rows on this surface). Provisional Financials keeps using ClassifiedTable directly
 * and is unaffected by this component.
 */
const AuditedStatementSection = ({ title, layout, tableData, loading, unit, defaultExpanded = true }: AuditedStatementSectionProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

    // UAT RECUR-26713 (Nishank #5): an empty statement collapses and shows an inline
    // "(No data available)" next to a muted heading — not a verbose message row below an
    // expanded heading. `hasData` mirrors AuditedStatementTable's own data check.
    const valueHeaders = (tableData?.headers ?? []).filter((header) => header.name !== 'Classification');
    const rows = (tableData?.rows ?? []).filter((row) => !isEmpty(row));
    const hasData = valueHeaders.length > 0 && rows.length > 0;
    const expanded = isExpanded && hasData;

    return (
        <div className={styles.Section}>
            <button
                type="button"
                className={styles.SectionHeader}
                onClick={() => hasData && setIsExpanded((prev) => !prev)}
                disabled={!hasData}
                data-testid="statement-section-header"
            >
                <img
                    src={caretRight}
                    alt=""
                    className={clsx(styles.Chevron, { [styles.ChevronExpanded]: expanded })}
                />
                <span
                    className={clsx(styles.SectionTitle, { [styles.SectionTitleMuted]: !hasData })}
                    data-testid="statement-section-title"
                >
                    {title}
                </span>
                {!hasData && <span className={styles.NoData}>(No data available)</span>}
            </button>

            {expanded && (
                <div className={styles.SectionBody}>
                    <AuditedStatementTable layout={layout} tableData={tableData} loading={loading} unit={unit} emptyMessage="" />
                </div>
            )}
        </div>
    );
};

export default AuditedStatementSection;
